import { CalendarClock } from "lucide-react";
import { DateTime } from "luxon";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { DateTimePickerPanel } from "@/components/ui/datetime-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DuePreset {
  label: string;
  /** Muted right-aligned context, e.g. "Wed, 9 AM" */
  hint: string;
  date: Date;
}

/**
 * Semantic defaults for a deadline. Times of day follow the common snooze
 * conventions: end of workday for "today", start of workday otherwise.
 */
function getDuePresets(): DuePreset[] {
  const now = DateTime.now();
  const at = (day: DateTime, hour: number) =>
    day.set({ hour, minute: 0, second: 0, millisecond: 0 });

  const presets: DuePreset[] = [];

  // "Today" only makes sense while there's still workday left.
  const todayFivePm = at(now, 17);
  if (now < todayFivePm.minus({ minutes: 30 })) {
    presets.push({ label: "Today", hint: "5:00 PM", date: todayFivePm.toJSDate() });
  }

  const tomorrow = at(now.plus({ days: 1 }), 9);
  presets.push({ label: "Tomorrow", hint: tomorrow.toFormat("ccc, h a"), date: tomorrow.toJSDate() });

  // End of this week (Friday); if it's already Friday or the weekend, skip —
  // "Next week" covers it.
  const friday = at(now.startOf("week").plus({ days: 4 }), 17);
  if (friday > now.plus({ days: 1 }).endOf("day")) {
    presets.push({ label: "End of week", hint: friday.toFormat("ccc, h a"), date: friday.toJSDate() });
  }

  const nextMonday = at(now.startOf("week").plus({ weeks: 1 }), 9);
  presets.push({
    label: "Next week",
    hint: nextMonday.toFormat("ccc, h a"),
    date: nextMonday.toJSDate(),
  });

  const inAWeek = at(now.plus({ weeks: 1 }), 9);
  presets.push({ label: "In a week", hint: inAWeek.toFormat("MMM d"), date: inAWeek.toJSDate() });

  return presets;
}

export interface DueDatePickerProps {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  /** Custom trigger; defaults to an outline button showing the current value. */
  trigger?: ReactNode;
  align?: "start" | "center" | "end";
  placeholder?: string;
  /** Class applied to the default trigger button. */
  className?: string;
}

/**
 * Due date picker following the analytics date-range picker pattern: a column
 * of semantic presets beside the full date/time wizard for custom deadlines.
 */
export function DueDatePicker({
  value,
  onChange,
  trigger,
  align = "end",
  placeholder = "No due date",
  className,
}: DueDatePickerProps) {
  const [open, setOpen] = useState(false);
  // Computed at open so the presets are fresh (not from a stale render).
  const presets = open ? getDuePresets() : [];

  const valueLabel = value
    ? DateTime.fromJSDate(value).toFormat(
      DateTime.fromJSDate(value).year === DateTime.now().year
        ? "MMM d, h:mm a"
        : "MMM d, yyyy, h:mm a",
    )
    : placeholder;

  function commit(date: Date | null) {
    onChange(date);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="outline"
            className={cn(
              "h-8 justify-start gap-2 px-3 font-normal shadow-none",
              !value && "text-muted-foreground",
              className,
            )}
          >
            <CalendarClock className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="truncate">{valueLabel}</span>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align={align} className="flex w-auto overflow-hidden p-0">
        <div className="flex w-48 flex-col gap-0.5 border-r border-border p-2">
          {presets.map((preset) => (
            <Button
              key={preset.label}
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 justify-between gap-3 px-2 font-normal"
              onClick={() => commit(preset.date)}
            >
              <span className="whitespace-nowrap">{preset.label}</span>
              <span className="whitespace-nowrap text-xs text-muted-foreground">{preset.hint}</span>
            </Button>
          ))}
          {value ? (
            <>
              <div className="my-1 h-px bg-border" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 justify-start px-2 font-normal text-muted-foreground"
                onClick={() => commit(null)}
              >
                No due date
              </Button>
            </>
          ) : null}
        </div>
        <DateTimePickerPanel
          value={value}
          onChange={onChange}
          reviewTitle="Due date"
          className="w-[240px]"
          onConfirm={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
